import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Download } from 'lucide-react';

/**
 * Generic data grid used by nearly every Admin page. Fetches `endpoint`,
 * accepts a column config, and gives an optional search box + status pills.
 *
 * Props:
 *   endpoint   — API path relative to /api (e.g. '/admin/orders')
 *   columns    — [{ key, label, render?, width?, testid? }]
 *   filters    — [{ key, label, options: [{value,label}] }]
 *   searchable — boolean
 *   csvEntity  — string; when present, adds an "Export CSV" button
 *   pageTitle
 *   testidBase
 *   onRowClick — (row) => void; optional
 */
export default function AdminList({
  endpoint, columns, filters = [], searchable = true,
  csvEntity, pageTitle, testidBase = 'admin-list',
  onRowClick,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState({});   // { filterKey: value }

  const params = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    Object.entries(selected).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [q, selected]);

  const load = () => {
    setLoading(true);
    api.get(endpoint, { params }).then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [endpoint, JSON.stringify(params)]);

  return (
    <div className="space-y-3" data-testid={testidBase}>
      <div className="flex items-center gap-2 flex-wrap">
        {pageTitle && <h2 className="font-display text-lg font-bold text-fab-text">{pageTitle}</h2>}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {searchable && (
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              data-testid={`${testidBase}-search`}
              className="bg-white border border-fab-primary-lighter rounded-lg px-2.5 py-1 text-[12px] outline-none w-52"
            />
          )}
          {filters.map((f) => (
            <select
              key={f.key} value={selected[f.key] || ''}
              onChange={(e) => setSelected((prev) => ({ ...prev, [f.key]: e.target.value }))}
              data-testid={`${testidBase}-filter-${f.key}`}
              className="bg-white border border-fab-primary-lighter rounded-lg px-2 py-1 text-[12px]"
            >
              <option value="">{f.label}</option>
              {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
          {csvEntity && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${csvEntity}.csv`}
              className="inline-flex items-center gap-1 bg-fab-wa/10 text-fab-primary text-[12px] font-semibold px-2 py-1 rounded-lg"
              data-testid={`${testidBase}-csv`}
            ><Download className="w-3.5 h-3.5" /> CSV</a>
          )}
          <span className="text-[11px] text-fab-text-muted" data-testid={`${testidBase}-count`}>
            {loading ? '' : `${items.length} row${items.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>

      <div className="bg-white border border-fab-primary-lighter rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead className="bg-fab-primary/5">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider" style={c.width ? { width: c.width } : {}}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-fab-text-muted"><Loader2 className="w-4 h-4 inline animate-spin" /> loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-fab-text-muted" data-testid={`${testidBase}-empty`}>No records</td></tr>
              ) : items.map((row, i) => (
                <tr
                  key={row.id || i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-t border-fab-primary-lighter/60 ${onRowClick ? 'hover:bg-fab-primary/5 cursor-pointer' : ''}`}
                  data-testid={`${testidBase}-row-${i}`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className="px-3 py-2 align-top text-fab-text" data-testid={`${testidBase}-cell-${c.key}-${i}`}>
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Compact chip helpers used across admin pages ────────────────────────────
export function StatusChip({ value, tone = 'neutral' }) {
  const tones = {
    green:   'bg-green-100 text-green-800',
    red:     'bg-red-100 text-red-800',
    orange:  'bg-orange-100 text-orange-800',
    blue:    'bg-blue-100 text-blue-800',
    purple:  'bg-purple-100 text-purple-800',
    neutral: 'bg-gray-100 text-gray-700',
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${tones[tone] || tones.neutral}`}>{value}</span>;
}
export function statusToneOrder(status) {
  return ({
    PENDING_PAYMENT: 'orange', AWAITING_VERIFICATION: 'blue',
    CONFIRMED: 'green', DISPATCHED: 'purple', DELIVERED: 'green',
    CANCELLED: 'red', REJECTED: 'red', PACKED: 'blue', SHIPPED: 'purple',
  })[status] || 'neutral';
}
export function statusTonePayment(status) {
  return ({ VERIFIED: 'green', SCREENSHOT_UPLOADED: 'blue', REJECTED: 'red',
            PENDING: 'orange', UNDERPAID: 'red' })[status] || 'neutral';
}
export function fmtINR(n) { return '₹' + Number(n || 0).toLocaleString('en-IN'); }
export function fmtDate(s) { return s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'; }
